import { isWeekend, parseISO } from 'date-fns';
import type { UsageDay, UsageSummary, UsageSummaryTotals } from 'c-sdk';

/**
 * The overview's switchable numbers (board 1): each is a cell in the summary strip and a series
 * the chart can show. `daily` is the field in `UsageSummary.daily`, `total` the headline number.
 */
export const OVERVIEW_METRICS = [
    'active_users',
    'active_organisations',
    'sessions',
    'time_ms',
    'actions',
    'campaigns_created',
    'live_campaigns',
] as const;

export type OverviewMetric = (typeof OVERVIEW_METRICS)[number];

export const totalFor = (totals: UsageSummaryTotals, metric: OverviewMetric): number =>
    totals[metric];

/** Time is charted in hours so the axis reads sensibly; everything else as counted. */
const chartValue = (metric: OverviewMetric, value: number) =>
    metric === 'time_ms' ? value / 3_600_000 : value;

/** Change on the previous period, as a whole percentage; null when there's nothing to compare. */
export const changeFor = (
    totals: UsageSummaryTotals,
    previous: UsageSummaryTotals,
    metric: OverviewMetric,
): number | null => {
    const was = totalFor(previous, metric);
    if (was === 0) return null;
    return Math.round(((totalFor(totals, metric) - was) / was) * 100);
};

/**
 * Where the chart's dashed "previous period" line sits: the previous period's average day, from
 * the API (a distinct count such as active users can't be averaged from its period total).
 */
export const previousLevel = (
    previousAverage: UsageSummary['previous_average'],
    metric: OverviewMetric,
): number => chartValue(metric, previousAverage[metric] ?? 0);

export type ChartPoint = { date: string; value: number; weekend: boolean };

/** The chosen metric per day. */
export const seriesFor = (daily: UsageDay[], metric: OverviewMetric): ChartPoint[] =>
    daily.map(day => ({
        date: day.date,
        value: chartValue(metric, day[metric]),
        weekend: isWeekend(parseISO(day.date)),
    }));
